import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { LOGOUT } from 'reducers/user'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'

import { MAIN_NAV, MAIN_NAV_BOTTOM, MAIN_NAV_MIDDLE } from '../../utils/data'
import { LogoWhiteIcon, LogoTextIcon } from '../../images'
import { LayoutContext } from '../../App'
import MainNavModalItem from './MainNavModalItem'
import { NavRowContent } from './NavRowContent'
import { useNavTooltip } from './useNavTooltip'
import AccountSwitcher from './AccountSwitcher/AccountSwitcher'
import { NotificationContext } from 'components/Notifications/notifications'

import { hasPermission } from 'hooks/useHasPermission'

/**
 * Logo motion, ported from fd-ui-prototype's side-nav.tsx (branch side-navbar).
 * Opacity and transform animate on different schedules, and only the arriving
 * state springs — the leaving one is quick and plain, so the two never look
 * like the same motion played backwards. EASE matches _main-nav.scss's
 * --nav-width-ease (the rail's own curve), reused here for the leaving layer
 * so it settles on the same beat as the rail closing around it.
 */
const DECELERATE = 'cubic-bezier(0, 0, 0.2, 1)'
const ACCELERATE = 'cubic-bezier(0.4, 0, 1, 1)'
const EASE = 'ease-in-out'
/** Damped-spring step response (ratio 0.45, ~1.25 oscillations) emitted as a
 *  linear() easing — overshoots to ~120%, dips to ~96%, settles at 100%. */
const SPRING =
  'linear(0, 0.0188, 0.0702, 0.1471, 0.2423, 0.3496, 0.4628, 0.577, 0.6878, 0.7918, 0.8862, 0.9692, 1.0397, 1.0971, 1.1415, 1.1735, 1.1939, 1.2038, 1.2047, 1.198, 1.1852, 1.1677, 1.147, 1.1243, 1.1009, 1.0776, 1.0554, 1.0347, 1.0163, 1.0002, 0.9868, 0.9761, 0.968, 0.9624, 0.9591, 0.9579, 0.9583, 0.9603, 0.9633, 0.9672, 0.9716, 0.9764, 0.9812, 0.9859, 1)'

/** Whichever layer is arriving springs in from `hiddenTransform`, starting
 *  100ms in (after the outgoing layer has faded), so the two never overlap
 *  at mismatched sizes. The leaving layer just recedes on the plain curve. */
function arrive(visible, hiddenTransform) {
  return {
    opacity: visible ? 1 : 0,
    transform: visible ? 'none' : hiddenTransform,
    transitionProperty: 'opacity, transform',
    transitionDuration: visible ? '200ms, 560ms' : '120ms, 400ms',
    transitionDelay: visible ? '100ms' : '0ms',
    transitionTimingFunction: visible ? `${DECELERATE}, ${SPRING}` : `${ACCELERATE}, ${EASE}`,
  }
}

const ToggleSidebarBtn = () => (
  <LayoutContext.Consumer>
    {({ sidebarExpanded, setSidebarExpanded }) => (
      <button
        className={`toggle-sidebar-btn ${sidebarExpanded ? 'expanded' : 'collapsed'}`}
        onClick={() => setSidebarExpanded(!sidebarExpanded)}
      >
        <i
          style={{
            marginRight: sidebarExpanded ? '0.125rem' : '0',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            color: '#FFFFFFCC',
          }}
          className={`fa-solid fa-chevron-${sidebarExpanded ? 'left' : 'right'}`}
        ></i>
      </button>
    )}
  </LayoutContext.Consumer>
)

const NavLinkItem = ({ item, t, sidebarExpanded, children }) => {
  const { linkRef, handleMouseEnter, handleMouseLeave, tooltipNode } = useNavTooltip({
    label: t(`Menu.${item.key}`),
    disabled: sidebarExpanded,
  })

  return (
    <li key={item.key} id={`main-nav-item-${item.key}`} className="main-nav__item white-icon mb-0">
      {item.external ? (
        <a
          ref={linkRef}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          title={item.title}
          className="main-nav__link"
          aria-label={t(`shared.menu.${item.key}`)}
          data-testid={`main-nav-link-${item.key}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </a>
      ) : item.path ? (
        <NavLink
          ref={linkRef}
          to={item.path}
          onClick={item.onClick ?? null}
          exact={item.path === '/' ? true : false}
          title={item.title}
          className="main-nav__link"
          activeClassName="main-nav__link--active"
          aria-label={t(`shared.menu.${item.key}`)}
          data-testid={`main-nav-link-${item.key}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </NavLink>
      ) : (
        <button
          ref={linkRef}
          onClick={item.onClick}
          title={item.title}
          className="main-nav__link w-100"
          aria-label={t(`shared.menu.${item.key}`)}
          data-testid={`main-nav-link-${item.key}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </button>
      )}

      {tooltipNode}
    </li>
  )
}

NavLinkItem.propTypes = {
  item: PropTypes.object,
  t: PropTypes.func,
  sidebarExpanded: PropTypes.bool,
  children: PropTypes.node.isRequired,
}

/**
 * Main navigation for desktop.
 * @Tags( shared, desktop, presentational )
 */
const MainNav = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const layout = useContext(LayoutContext)
  const notificationState = useContext(NotificationContext)

  const notificationsBadgeText = notificationState.stats?.unread
    ? Math.min(99, notificationState.stats?.unread)
    : null

  const displayNavBarItem = (item) => {
    if (item?.navType === 'modal') {
      return <MainNavModalItem key={item.key} item={item} layout={layout} />
    }

    return (
      <NavLinkItem key={item.key} item={item} t={t} sidebarExpanded={layout.sidebarExpanded}>
        <NavRowContent
          item={item}
          t={t}
          sidebarExpanded={layout.sidebarExpanded}
          badgeText={item.key === 'notifications' ? notificationsBadgeText : null}
        />
      </NavLinkItem>
    )
  }

  return (
    <nav className={`main-nav main-nav-${layout.sidebarExpanded ? 'expanded' : 'collapsed'}`}>
      <ul role="navigation">
        <li className="main-nav__item main-nav__logo">
          <NavLink
            to="/"
            className={`main-nav__link ${layout.sidebarExpanded ? 'main-nav__link_expanded' : ''} brand-logo`}
          >
            <div className="brand-logo__inner">
              <LogoWhiteIcon className="brand-logo__mark" height="30" alt="fd" />

              <span
                className={`brand-logo__label-slot ${layout.sidebarExpanded ? 'brand-logo__label-slot--expanded' : ''}`}
              >
                <LogoTextIcon
                  className="brand-logo__label"
                  style={arrive(layout.sidebarExpanded, 'scale(0.84)')}
                  height="30"
                  alt="future demand"
                />
              </span>
            </div>
          </NavLink>
        </li>

        {MAIN_NAV.map((item) =>
          hasPermission(item.permissionName) ? displayNavBarItem(item) : null,
        )}

        <hr className="my-4 mx-2 bg-white" style={{ opacity: 0.3 }} />

        {MAIN_NAV_MIDDLE.map((item) => displayNavBarItem(item))}

        <div className="mt-auto mb-3">
          <hr className="my-4 mx-2 bg-white" style={{ opacity: 0.3 }} />

          <AccountSwitcher />

          {MAIN_NAV_BOTTOM.map((item) =>
            displayNavBarItem(
              item.key === 'logout'
                ? {
                  ...item,
                  onClick: () => {
                    dispatch({ type: LOGOUT })
                  },
                }
                : item,
            ),
          )}
        </div>
      </ul>
      <ToggleSidebarBtn />
    </nav>
  )
}

MainNav.propTypes = {}

MainNav.defaultProps = {}

export default MainNav
